import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { getLectureChapters, formatTime, Chapter } from '../../../services/lectureApi';

interface StreamingChaptersProps {
  onSeekToTime: (seconds: number) => void;
  lectureId?: string; // 강의 ID 추가
}

const StreamingChapters: React.FC<StreamingChaptersProps> = ({ onSeekToTime, lectureId }) => {
  const [chapters, setChapters] = useState<Chapter[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // 컴포넌트 마운트 시 챕터 데이터 로드
  useEffect(() => {
    const fetchChapters = async () => {
      if (!lectureId) {
        setError('강의 ID가 없습니다.');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        console.log('Fetching chapters for lecture ID:', lectureId);
        
        const response = await getLectureChapters(parseInt(lectureId));
        console.log('Chapters response:', response);
        
        setChapters(response.data);
      } catch (err) {
        setError('챕터 목록을 불러오는데 실패했습니다.');
        console.error('Chapter fetch error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchChapters();
  }, [lectureId]); // lectureId 의존성 추가

  const handleChapterClick = (timecode: number) => {
    // 챕터 클릭 시 비디오 시간 이동
    console.log(`Chapter clicked - jump to time: ${timecode}s`);
    onSeekToTime(timecode);
  };

  // 로딩 상태 표시
  if (loading) {
    return (
      <ChaptersContainer>
        <LoadingMessage>챕터 목록을 불러오는 중...</LoadingMessage>
      </ChaptersContainer>
    );
  }

  // 에러 상태 표시
  if (error) {
    return (
      <ChaptersContainer>
        <ErrorMessage>{error}</ErrorMessage>
      </ChaptersContainer>
    );
  }

  // 챕터 목록이 없는 경우
  if (chapters.length === 0) {
    return (
      <ChaptersContainer>
        <EmptyMessage>등록된 챕터가 없습니다.</EmptyMessage>
      </ChaptersContainer>
    );
  }

  return (
    <ChaptersContainer>
      <ChaptersList>
        {chapters.map((chapter, index) => (
          <ChapterItem 
            key={chapter.uri} 
            onClick={() => handleChapterClick(chapter.timecode)}
          >
            <ChapterContent>
              <IconSection>
                <IconWrapper>
                  <PlayIcon />
                </IconWrapper>
              </IconSection>
              <ContentSection>
                <MainContent>
                  <ChapterTitle>{chapter.title}</ChapterTitle>
                </MainContent>
                <DurationSection>
                  <ChapterDuration>{formatTime(chapter.timecode)}</ChapterDuration>
                </DurationSection>
              </ContentSection>
            </ChapterContent>
          </ChapterItem>
        ))}
      </ChaptersList>
    </ChaptersContainer>
  );
};

const ChaptersContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 12px;
`;

const LoadingMessage = styled.div`
  width: 100%;
  padding: 40px 16px;
  text-align: center;
  color: var(--Label-Alternative, rgba(55, 56, 60, 0.61));
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 24px;
`;

const ErrorMessage = styled.div`
  width: 100%;
  padding: 40px 16px;
  text-align: center;
  color: #FF4444;
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 24px;
`;

const EmptyMessage = styled.div`
  width: 100%;
  padding: 40px 16px;
  text-align: center;
  color: var(--Label-Alternative, rgba(55, 56, 60, 0.61));
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 24px;
`;

const ChaptersList = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 10px;
`;

const ChapterItem = styled.div`
  width: 100%;
  border-radius: 12px;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(112, 115, 124, 0.04);
  }
  
  &:focus {
    background: rgba(112, 115, 124, 0.04);
    outline: none;
  }
  
  &:active {
    background: rgba(112, 115, 124, 0.08);
  }
`;

const ChapterContent = styled.div`
  flex: 1;
  width: 100%;
  padding: 12px 16px;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 8px;
  position: relative;
`;

const IconSection = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  overflow: hidden;
`;

const IconWrapper = styled.div`
  padding-right: 8px;
  display: flex;
  justify-content: flex-start;
  align-items: center;
`;

const PlayIcon = styled.div`
  width: 48px;
  height: 48px;
  padding: 8px;
  background: var(--To-Be-Doctor-99, #F0F7F7);
  overflow: hidden;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  position: relative;
  transition: all 0.2s ease;
  
  &::after {
    content: '';
    width: 32px;
    height: 32px;
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    background-image: url("data:image/svg+xml,%3Csvg width='32' height='32' viewBox='0 0 32 32' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M14.4449 24.8407C12.3339 26.0941 11.2784 26.7208 10.4106 26.6371C9.65382 26.5641 8.96418 26.1715 8.51504 25.5581C8 24.8546 8 23.6271 8 21.172V10.8295C8 8.37438 8 7.14684 8.51504 6.44337C8.96418 5.82992 9.65382 5.43736 10.4106 5.36435C11.2784 5.28063 12.3339 5.90734 14.4449 7.16076L23.1544 12.332C25.1896 13.5404 26.2072 14.1446 26.5514 14.928C26.8518 15.6116 26.8518 16.3898 26.5514 17.0734C26.2072 17.8568 25.1896 18.461 23.1544 19.6694L14.4449 24.8407Z' fill='%23678B8C'/%3E%3C/svg%3E");
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center;
  }
`;

const ContentSection = styled.div`
  flex: 1;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 8px;
`;

const MainContent = styled.div`
  flex: 1;
  display: flex;
  justify-content: flex-start;
  align-items: center;
`;

const ChapterTitle = styled.div`
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 4px;
  
  min-height: 24px;
  color: var(--Label-Normal, #171719);
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 500;
  line-height: 24px;
  letter-spacing: 0.09px;
  word-wrap: break-word;
  transition: all 0.2s ease;
`;

const DurationSection = styled.div`
  overflow: hidden;
  display: flex;
  justify-content: flex-end;
  align-items: center;
`;

const ChapterDuration = styled.div`
  padding-left: 8px;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  
  text-align: right;
  color: var(--Label-Alternative, rgba(55, 56, 60, 0.61));
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0.09px;
  word-wrap: break-word;
  transition: all 0.2s ease;
`;

export default StreamingChapters;
